import { convertCentsToDollars } from '@vendasta/shared';
import { getLocaleCode } from '@vendasta/galaxy/utility/locale';
import { getCurrencySymbol } from '@vendasta/galaxy/utility/currency-utils';
import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'priceInDollars',
  standalone: false,
})
// Returns price string in the format $10.00, without the currency
/**
 * @param value {string} the numerical value that is being converted to a currency string
 * @param currencyCode {string} Three character code to represent the currency (ISO 4217) ex) USD, CAD, AUS
 * @param locale {string} A locale code for the locale format rules to use. (ISO 639-1 w/ optional two character region subtag) ex) en, en-US, en-CA, fr-CA, fr
 * @returns {string} Returns a string of the localized currency value with the currency symbol for the given currency code. Includes currency code at the end if currency symbol is "$"
 */
export class PriceInDollarsPipe implements PipeTransform {
  transform(value: number, currencyCode = 'USD', locale?: string): string {
    if (!value && value !== 0) {
      return '';
    }

    if (!currencyCode) {
      return '';
    }

    const amountInDollars = convertCentsToDollars(value);

    if (!locale) {
      locale = getLocaleCode();
    }

    let formattedPrice;
    try {
      formattedPrice = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol',
      }).format(amountInDollars);
    } catch {
      formattedPrice = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'symbol',
      }).format(amountInDollars);
    }

    const currencySymbol = getCurrencySymbol(currencyCode);

    // sometimes the Number format doesn't return a string with a currency symbol. Instead, it returns the currency code.
    // if that's the case we'll get currency symbol and replace the currency code with the symbol.
    // ex) currency is "CZK", number format returns "CZK 3", so we swap out the currency code for the symbol (Kč)
    if (formattedPrice.includes(currencyCode)) {
      formattedPrice = formattedPrice.replace(currencyCode, currencySymbol); // Ex. Replace EURO40 with €40 (?) >> add unit tests
    }

    return formattedPrice;
  }
}
